import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BreakAlert({ user, onPause, onResume }) {
  const [config, setConfig] = useState(null);
  const [currentBreak, setCurrentBreak] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [dismissedBreaks, setDismissedBreaks] = useState([]);
  const [autoPaused, setAutoPaused] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (!config) return;

    const checkBreaks = () => {
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      
      // NOTA: Almoço foi REMOVIDO da automação - deve ser selecionado manualmente pelo operador
      // As pausas da manhã e tarde (pausas_adicionais) continuam automáticas

      // Check additional breaks - AUTO PAUSE sem confirmação
      config.pausas_adicionais?.forEach((pausa, index) => {
        if (pausa.inicio === currentTime && !dismissedBreaks.includes(`pausa_${index}`)) {
          const breakInfo = {
            type: `pausa_${index}`,
            name: pausa.descricao || 'Pausa',
            start: pausa.inicio,
            end: pausa.fim,
            icon: '☕',
            message: `Hora da pausa: ${pausa.descricao || 'pausa programada'}`
          };
          setCurrentBreak(breakInfo);
          setShowAlert(true);
          setAutoPaused(true);
          setDismissedBreaks(prev => [...prev, `pausa_${index}`]);
          // Iniciar pausa automaticamente
          if (onPause) {
            onPause(breakInfo);
          }
        }
      });
      
      // Verificar se a pausa terminou para fechar o alert
      if (autoPaused && currentBreak) {
        if (currentTime >= currentBreak.end) {
          setShowAlert(false);
          setAutoPaused(false);
          setCurrentBreak(null);
          // Notificar que a pausa terminou
          if (onResume) {
            onResume(currentBreak);
          }
        }
      }
    };

    checkBreaks();
    const interval = setInterval(checkBreaks, 60000);

    return () => clearInterval(interval);
  }, [config, dismissedBreaks, autoPaused, currentBreak, onPause, onResume]);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/configuracao`);
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  // Fechar o aviso manualmente (a pausa já foi iniciada automaticamente)
  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  // Aviso informativo de pausa automática (sem opção de cancelar)
  if (showAlert && currentBreak && autoPaused) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm bg-white rounded-3xl shadow-float overflow-hidden animate-in fade-in zoom-in duration-300">
          {/* Header com gradiente */}
          <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-6 text-center">
            <span className="text-6xl mb-4 block animate-bounce">{currentBreak.icon}</span>
            <h2 className="text-2xl font-heading font-bold text-amber-800">
              {currentBreak.name}
            </h2>
            <p className="text-amber-700 text-sm mt-1">
              {currentBreak.start} - {currentBreak.end}
            </p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <Clock className="h-8 w-8 text-amber-600 mx-auto mb-2" />
              <p className="text-lg font-semibold text-amber-800">
                Tempo Pausado Automaticamente
              </p>
              <p className="text-sm text-amber-600 mt-1">
                {currentBreak.message}
              </p>
            </div>

            <p className="text-center text-stone-500 text-sm">
              O tempo de produção foi pausado automaticamente durante este intervalo.
              <br />
              A contagem retoma às <strong>{currentBreak.end}</strong>.
            </p>

            <Button
              className="w-full h-12 rounded-full bg-amber-500 text-white hover:bg-amber-600"
              onClick={handleCloseAlert}
            >
              Entendido
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return null;
}
